export const ROOM_ATMOSPHERE_STORAGE_KEY =
  "convoful.room-atmosphere.v1";

export const ROOM_ATMOSPHERE_IDS = [
  "velvet",
  "candlelight",
  "moonlit",
  "daybreak",
] as const;

export type RoomAtmosphereID = typeof ROOM_ATMOSPHERE_IDS[number];

export interface RoomAtmosphereOption {
  id: RoomAtmosphereID;
  label: string;
  description: string;
}

export interface RoomAtmosphereStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const DEFAULT_ROOM_ATMOSPHERE: RoomAtmosphereID = "velvet";

export const ROOM_ATMOSPHERE_OPTIONS: readonly RoomAtmosphereOption[] = [
  {
    id: "velvet",
    label: "Velvet",
    description: "Soft plum",
  },
  {
    id: "candlelight",
    label: "Candlelight",
    description: "Warm amber",
  },
  {
    id: "moonlit",
    label: "Moonlit",
    description: "Quiet blue",
  },
  {
    id: "daybreak",
    label: "Daybreak",
    description: "Gentle dawn",
  },
] as const;

const roomAtmosphereIDSet = new Set<string>(ROOM_ATMOSPHERE_IDS);
const roomAtmosphereSchemaVersion = 1;

function isRoomAtmosphereID(value: unknown): value is RoomAtmosphereID {
  return typeof value === "string" && roomAtmosphereIDSet.has(value);
}

function clearInvalidPreference(storage: RoomAtmosphereStorage): void {
  try {
    storage.removeItem(ROOM_ATMOSPHERE_STORAGE_KEY);
  } catch {
    // A visual preference must never keep the product from opening.
  }
}

export function loadRoomAtmosphere(
  storage: RoomAtmosphereStorage,
): RoomAtmosphereID {
  let stored: string | null;
  try {
    stored = storage.getItem(ROOM_ATMOSPHERE_STORAGE_KEY);
  } catch {
    return DEFAULT_ROOM_ATMOSPHERE;
  }
  if (stored === null) return DEFAULT_ROOM_ATMOSPHERE;

  try {
    const parsed: unknown = JSON.parse(stored);
    if (
      typeof parsed !== "object"
      || parsed === null
      || Array.isArray(parsed)
    ) {
      clearInvalidPreference(storage);
      return DEFAULT_ROOM_ATMOSPHERE;
    }
    const record = parsed as Record<string, unknown>;
    const keys = Object.keys(record).sort();
    if (
      keys.length !== 2
      || keys[0] !== "atmosphere"
      || keys[1] !== "version"
      || record.version !== roomAtmosphereSchemaVersion
      || !isRoomAtmosphereID(record.atmosphere)
    ) {
      clearInvalidPreference(storage);
      return DEFAULT_ROOM_ATMOSPHERE;
    }
    return record.atmosphere;
  } catch {
    clearInvalidPreference(storage);
    return DEFAULT_ROOM_ATMOSPHERE;
  }
}

export function saveRoomAtmosphere(
  storage: RoomAtmosphereStorage,
  atmosphere: RoomAtmosphereID,
): void {
  storage.setItem(
    ROOM_ATMOSPHERE_STORAGE_KEY,
    JSON.stringify({
      version: roomAtmosphereSchemaVersion,
      atmosphere,
    }),
  );
}

export function resetRoomAtmosphere(
  storage: RoomAtmosphereStorage,
): RoomAtmosphereID {
  storage.removeItem(ROOM_ATMOSPHERE_STORAGE_KEY);
  return DEFAULT_ROOM_ATMOSPHERE;
}
